import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { extractValue } from '@/utils/rxjs/helpers/rxjs.helpers';
import { groupBy } from '@/utils/toolkit/object/group-by.utils';
import { extractFromRecord } from '@/utils/typing/branded.type';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import type { primitive } from '@/utils/typing/primitive.type';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, NgZone, type OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type * as Highcharts from 'highcharts';
import { BehaviorSubject, combineLatest, defer, NEVER, type Observable } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { sseTurquoiseColor } from '../../../constants/color';
import type {
    BusinessIntelligenceDataPoint,
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberCode
} from '../../../interfaces/BusinessIntelligence.interface';
import { HighchartsStaticProvider } from '../../../providers/highcharts/highcharts.static.provider';
import { HighchartsChartComponent } from '../../highcharts-chart/highcharts-chart';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';
import type { BusinessIntelligenceRepresentationComponentConfig } from './../../../interfaces/BusinessIntelligenceRepresentation.interface';

export interface BusinessIntelligenceScatterChartComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    xAxisMeasureCode: BusinessIntelligenceMeasureCode;
    yAxisMeasureCode: BusinessIntelligenceMeasureCode;
    pointSizeMeasureCode: BusinessIntelligenceMeasureCode | null;
    groupByDimensionCode: BusinessIntelligenceDimensionCode;
    segmentationDimensionCode: BusinessIntelligenceDimensionCode;
    clickDimensionCode: BusinessIntelligenceDimensionCode | null;
}

const RADIUS_MIN = 4;
const RADIUS_MAX = 30;
const DEFAULT_RADIUS = 3;

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-scatter-chart.scss'],
    selector: 'business-intelligence-scatter-chart',
    templateUrl: 'business-intelligence-scatter-chart.html',
    imports: [HighchartsChartComponent, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligenceScatterChartComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligenceScatterChartComponentConfig>
    implements OnInit
{
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _ngZone = inject(NgZone);

    @Output() clickEvent: EventEmitter<any> = new EventEmitter();

    noData: boolean = false;

    chartConfig$!: Observable<Highcharts.Options>;

    tooltip$ = new BehaviorSubject<{ tooltip: any; content$: Observable<string> } | null>(null);

    ngOnInit() {
        this.tooltip$
            .pipe(
                filter(isNotNullish),
                debounceTime(0),
                switchMap(({ tooltip, content$ }) => content$.pipe(map(content => ({ tooltip, content })))),
                untilDestroyed(this)
            )
            .subscribe(({ tooltip, content }) => {
                tooltip.label.attr({
                    text: content
                });
            });

        const tooltip$ = this.tooltip$;

        this.chartConfig$ = combineLatest([this._representationData$, this._config$]).pipe(
            // replace later with proper error handling for wrong configs
            filter(
                ([representationData, config]) =>
                    representationData && !!Object.keys(representationData.metadata).length && isNotNullish(config)
            ),
            tap(([representationData, config]) => {
                const meaningfulData = representationData.data.filter(
                    this.isDataPointMeaningfulInChart([config.xAxisMeasureCode, config.yAxisMeasureCode])
                );
                this.noData = !meaningfulData.length;
            }),
            switchMap(([representationData, config]) => {
                return defer(() => {
                    const meaningfulData = representationData.data.filter(
                        this.isDataPointMeaningfulInChart([config.xAxisMeasureCode, config.yAxisMeasureCode])
                    );

                    let radiusByPointSize: Record<string, number> = {};
                    if (config.pointSizeMeasureCode) {
                        radiusByPointSize = this.getRadiusList(
                            meaningfulData.map(dp => extractFromRecord(dp.measures, config.pointSizeMeasureCode!))
                        );
                    }

                    const getPoint = (
                        x: number,
                        y: number,
                        pointSize: number | null,
                        name: Observable<string>,
                        color: string | Observable<string>,
                        clickDimension: primitive | null
                    ) => {
                        if (isNotNullish(x) && isNotNullish(y)) {
                            return {
                                x,
                                y,
                                name,
                                color,
                                n: pointSize,
                                marker: { radius: pointSize ? radiusByPointSize[pointSize] : DEFAULT_RADIUS },
                                clickValue: clickDimension
                            };
                        }
                    };

                    const generateSeriesData = (
                        seriesData: BusinessIntelligenceDataPoint[],
                        color: string | Observable<string>,
                        seriesCode: primitive
                    ) => {
                        return seriesData
                            .map(dp => {
                                return getPoint(
                                    extractFromRecord(dp.measures, config.xAxisMeasureCode),
                                    extractFromRecord(dp.measures, config.yAxisMeasureCode),
                                    config.pointSizeMeasureCode ? extractFromRecord(dp.measures, config.pointSizeMeasureCode) : null,
                                    seriesCode
                                        ? combineLatest([
                                              representationData.metadata[
                                                  config.groupByDimensionCode
                                              ].valuesFormatting.formatAsStandaloneText$(
                                                  extractFromRecord(dp.dimensions, config.groupByDimensionCode)
                                              ),
                                              representationData.metadata[
                                                  config.segmentationDimensionCode
                                              ].valuesFormatting.formatAsStandaloneText$(seriesCode)
                                          ]).pipe(
                                              map(([groupByName, segmentationName]) => {
                                                  return groupByName + ' - ' + segmentationName;
                                              })
                                          )
                                        : representationData.metadata[config.groupByDimensionCode].valuesFormatting.formatAsStandaloneText$(
                                              extractFromRecord(dp.dimensions, config.groupByDimensionCode)
                                          ),
                                    color,
                                    config.clickDimensionCode ? extractFromRecord(dp.dimensions, config.clickDimensionCode) : null
                                );
                            })
                            .filter(isNotNullish);
                    };

                    let series: any[];
                    let areSeriesMeaningful: boolean;

                    if (config.segmentationDimensionCode) {
                        series = Array.from(
                            groupBy(meaningfulData, dataPoint =>
                                extractFromRecord(dataPoint.dimensions, config.segmentationDimensionCode)
                            ).entries()
                        ).map(([seriesCode, values]) => {
                            const color =
                                representationData.metadata[config.segmentationDimensionCode].valuesFormatting.formatAsColor$(seriesCode);
                            return {
                                name: representationData.metadata[
                                    config.segmentationDimensionCode
                                ].valuesFormatting.formatAsStandaloneText$(seriesCode),
                                color,
                                data: generateSeriesData(values, color, seriesCode),
                                point: {
                                    events: {
                                        click: (event: any) => {
                                            if (!config.clickDimensionCode) {
                                                return;
                                            }
                                            this._ngZone.run(() => {
                                                this.clickEvent.emit(event.point.clickValue);
                                            });
                                        }
                                    }
                                }
                            };
                        });
                        areSeriesMeaningful = true;
                    } else {
                        series = [
                            {
                                name: representationData.metadata[config.groupByDimensionCode].getName$(),
                                data: generateSeriesData(meaningfulData, sseTurquoiseColor, null),
                                point: {
                                    events: {
                                        click: (event: any) => {
                                            if (!config.clickDimensionCode) {
                                                return;
                                            }
                                            this._ngZone.run(() => {
                                                this.clickEvent.emit(event.point.clickValue);
                                            });
                                        }
                                    }
                                }
                            }
                        ];
                        areSeriesMeaningful = false;
                    }

                    return combineLatestDeepObject(
                        this._highchartsStaticProvider.buildConfig({
                            chart: {
                                type: 'scatter',
                                zooming: {
                                    type: 'xy'
                                }
                            },
                            boost: {
                                // if necessary, we could work so that the chart works in boost mode.
                                // See that if performance issues arise
                                seriesThreshold: 5000
                            },
                            plotOptions: {
                                scatter: {
                                    marker: {
                                        states: {
                                            hover: {
                                                enabled: true,
                                                lineColor: 'rgb(100,100,100)'
                                            }
                                        },
                                        symbol: 'circle'
                                    },
                                    states: {
                                        hover: {
                                            marker: {
                                                enabled: false
                                            }
                                        }
                                    }
                                },
                                series: {
                                    cursor: isNotNullish(config.clickDimensionCode) ? 'pointer' : 'default',
                                    // if necessary, we could work so that the chart works in turbo / boost mode.
                                    // Check that if performance issues arise
                                    turboThreshold: 0, // disabled
                                    boostThreshold: 0 // disabled
                                }
                            },
                            xAxis: {
                                title: {
                                    enabled: true,
                                    text: representationData.metadata[config.xAxisMeasureCode].getName$()
                                },
                                labels: {
                                    formatter(): string | undefined {
                                        // TODO : handle async updates
                                        return extractValue(
                                            representationData.metadata[config.xAxisMeasureCode].valuesFormatting.formatAsStandaloneText$(
                                                (this as any).value
                                            )
                                        );
                                    }
                                }
                            },
                            yAxis: {
                                title: {
                                    text: representationData.metadata[config.yAxisMeasureCode].getName$()
                                },
                                labels: {
                                    formatter(): string | undefined {
                                        // TODO : handle async updates
                                        return extractValue(
                                            representationData.metadata[config.yAxisMeasureCode].valuesFormatting.formatAsStandaloneText$(
                                                (this as any).value
                                            )
                                        );
                                    }
                                }
                            },

                            tooltip: {
                                formatter(tooltip: any) {
                                    tooltip$.next({
                                        tooltip,
                                        content$: config.pointSizeMeasureCode
                                            ? combineLatest([
                                                  representationData.metadata[
                                                      config.xAxisMeasureCode
                                                  ].valuesFormatting.formatAsStandaloneText$((this as any).point.x),
                                                  representationData.metadata[config.xAxisMeasureCode].getName$(),
                                                  representationData.metadata[
                                                      config.yAxisMeasureCode
                                                  ].valuesFormatting.formatAsStandaloneText$((this as any).point.y),
                                                  representationData.metadata[config.yAxisMeasureCode].getName$(),
                                                  representationData.metadata[
                                                      config.pointSizeMeasureCode
                                                  ].valuesFormatting.formatAsStandaloneText$((this as any).point.n),
                                                  representationData.metadata[config.pointSizeMeasureCode].getName$()
                                              ]).pipe(
                                                  map(
                                                      ([xValue, xName, yValue, yName, pointSizeValue, pointSizeName]) =>
                                                          `<b>${
                                                              (this as any).point.name
                                                          }</b><br/> ${xName}: <b>${xValue}</b> <br/> ${yName}: <b>${yValue}</b> <br/> ${pointSizeName}: <b>${pointSizeValue}</b>`
                                                  )
                                              )
                                            : combineLatest([
                                                  representationData.metadata[
                                                      config.xAxisMeasureCode
                                                  ].valuesFormatting.formatAsStandaloneText$((this as any).point.x),
                                                  representationData.metadata[config.xAxisMeasureCode].getName$(),
                                                  representationData.metadata[
                                                      config.yAxisMeasureCode
                                                  ].valuesFormatting.formatAsStandaloneText$((this as any).point.y),
                                                  representationData.metadata[config.yAxisMeasureCode].getName$()
                                              ]).pipe(
                                                  map(
                                                      ([xValue, xName, yValue, yName]) =>
                                                          `<b>${
                                                              (this as any).point.name
                                                          }</b><br/> ${xName}: <b>${xValue}</b> <br/> ${yName}: <b>${yValue}</b>`
                                                  )
                                              )
                                    });
                                    return;
                                }
                            },
                            legend: {
                                enabled: areSeriesMeaningful,
                                maxHeight: 55,
                                symbolHeight: 10
                            },
                            series
                        })
                    );
                }).pipe(
                    catchError(err => {
                        console.error('Error while updating histogram config ::', err);
                        return NEVER;
                    })
                );
            })
        );
    }

    private getRadiusList(pointSizeList: number[]): Record<string, number> {
        const pointSizeMax = Math.max(...pointSizeList);
        const pointSizeMin = Math.min(...pointSizeList);

        return pointSizeList.reduce(
            (acc, value) => {
                if (!acc[value]) {
                    acc[value] = ((value - pointSizeMin) / (pointSizeMax - pointSizeMin)) * (RADIUS_MAX - RADIUS_MIN) + RADIUS_MIN;
                }
                return acc;
            },
            {} as Record<string, number>
        );
    }

    private isDataPointMeaningfulInChart(
        measureCodes: BusinessIntelligenceMeasureCode[]
    ): (dataPoint: BusinessIntelligenceDataPoint) => boolean {
        return (dataPoint: BusinessIntelligenceDataPoint) =>
            measureCodes.some(measureCode => isNotNullish(extractFromRecord(dataPoint.measures, measureCode)));
    }

    getExportMembers(config: BusinessIntelligenceScatterChartComponentConfig): BusinessIntelligenceMemberCode[] {
        return [
            config.groupByDimensionCode,
            ...(config.segmentationDimensionCode ? [config.segmentationDimensionCode] : []),
            config.xAxisMeasureCode,
            config.yAxisMeasureCode,
            ...(config.pointSizeMeasureCode ? [config.pointSizeMeasureCode] : [])
        ];
    }
}
